import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CategoryIcon from '@mui/icons-material/Category';
import ComputerIcon from '@mui/icons-material/Computer';
import GetAppIcon from '@mui/icons-material/GetApp';
import ReportIcon from '@mui/icons-material/Report';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import {
  getSecurityComplianceDetailHref,
  getSecurityEndpointDetailsHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import type { WazuhComplianceControlItem, WazuhComplianceEndpointItem } from 'src/types/mindtime/wazuhCompliance';
import type { Column, TableData } from 'src/types/table';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrGlobalScopeFilters,
  EdrKpiCard,
  edrMonoStyle,
  edrMutedStyle,
  EdrPageHeader,
  EdrSeverityPill,
  EdrStatusPill,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../shared/edrPrimitives';
import {
  complianceRiskToLevel,
  complianceStatusToTone,
  getComplianceControlId,
  toComplianceTitle,
} from './complianceDetailModel';
import { COMPLIANCE_CATEGORY_FILTER_OPTIONS } from './complianceFrameworkOptions';
import { useComplianceFrameworkFilterOptions } from './useComplianceFrameworkFilterOptions';
import { useComplianceData, useComplianceExport, type ComplianceSortParams } from './useComplianceData';


const getComplianceScoreColor = (score: number) => {
  if (score >= 80) return EDR_COLORS.success;
  if (score >= 50) return EDR_COLORS.medium;
  return EDR_COLORS.critical;
};

/** Local score + bar cell (same idea as VulnerabilityDetection CvssCell). */
const ComplianceScoreCell: FC<{ score?: number | null }> = ({ score }) => {
  if (typeof score !== 'number' || Number.isNaN(score)) {
    return <span style={edrMutedStyle}>—</span>;
  }
  const color = getComplianceScoreColor(score);
  const pct = Math.min(100, Math.max(0, score));
  return (
    <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 96 }}>
      <span style={{ ...edrMonoStyle, fontWeight: 700, color, minWidth: 36 }}>
        {`${Math.round(score)}%`}
      </span>
      <Box
        style={{
          flex: 1,
          height: 4,
          borderRadius: 2,
          background: EDR_COLORS.slate200,
          minWidth: 48,
          maxWidth: 72,
        }}
      >
        <Box style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: color }} />
      </Box>
    </Box>
  );
};

const TITLE_MAX_LEN = 60;

const TruncatedTitle: FC<{ text?: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const value = text?.trim() ?? '';
  if (!value) return <span style={edrMutedStyle}>—</span>;

  const canExpand = value.length > TITLE_MAX_LEN;
  const shown = canExpand && !expanded ? `${value.slice(0, TITLE_MAX_LEN)}…` : value;

  if (!canExpand) {
    return (
      <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>{shown}</span>
    );
  }

  return (
    <button
      type="button"
      title={expanded ? undefined : value}
      aria-expanded={expanded}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded((v) => !v);
      }}
      style={{
        fontWeight: 600,
        color: EDR_COLORS.textPrimary,
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        maxWidth: expanded ? undefined : 280,
        whiteSpace: expanded ? 'normal' : 'nowrap',
        wordBreak: expanded ? 'break-word' : undefined,
      }}
    >
      {shown}
    </button>
  );
};

const FrameworkTag: FC<{ value: string }> = ({ value }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 600,
      background: EDR_COLORS.brand50,
      color: EDR_COLORS.brand600,
      border: `1px solid ${EDR_COLORS.brand200}`,
      whiteSpace: 'nowrap',
    }}
  >
    {value}
  </span>
);

const FrameworkTags: FC<{ values?: string[] }> = ({ values }) => {
  const items = (values ?? []).filter(Boolean);
  if (!items.length) return <span style={edrMutedStyle}>—</span>;
  return (
    <Box display="flex" flexWrap="wrap" style={{ gap: 4, maxWidth: 180 }}>
      {items.map((value) => (
        <FrameworkTag key={value} value={value} />
      ))}
    </Box>
  );
};

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Configuration Compliance', to: getSecurityNavItemPath('configuration-compliance') ?? '' },
  ],
};

const STATUS_OPTIONS = [
  { label: 'Pass', value: 'pass' },
  { label: 'Fail', value: 'fail' },
  { label: 'Needs Review', value: 'needs_review' },
];

const ConfigurationCompliance: FC = () => {
  const navigate = useNavigate();
  const { setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.configurationCompliance);
  const {
    framework = '',
    status = '',
    category = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const frameworkOptions = useComplianceFrameworkFilterOptions(organizationId || undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortParams, setSortParams] = useState<ComplianceSortParams | null>(null);
  const [resetSortFlag, setResetSortFlag] = useState(false);
  const skipOrgFrameworkReset = useRef(true);

  useEffect(() => {
    if (skipOrgFrameworkReset.current) {
      skipOrgFrameworkReset.current = false;
      return;
    }
    setFilter('framework', '');
    setPage(0);
  }, [organizationId, setFilter]);
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'configuration-compliance');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const canEndpointCompliance = canWidget('endpointCompliance');
  const showUnavailable =
    permissionsResolved && !canSummary && !canList && !canEndpointCompliance;

  const complianceFilters = useMemo(
    () => ({
      framework,
      status,
      category,
      organizationId,
    }),
    [framework, status, category, organizationId]
  );

  const { loading, summary, controls, endpoints: endpointsPaged } = useComplianceData(
    page,
    rowsPerPage,
    search,
    complianceFilters,
    sortParams
  );
  const { exporting, handleExport } = useComplianceExport(search, complianceFilters, sortParams);

  const items = controls?.items ?? [];
  const totalRecords = controls?.totalRecords ?? 0;
  const complianceEndpoints = endpointsPaged?.items ?? [];
  const complianceEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const openComplianceDetail = useCallback(
    (item: WazuhComplianceControlItem) => {
      const id = getComplianceControlId(item);
      if (!id) return;
      navigate(getSecurityComplianceDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const toolbarFilters = useMemo((): EdrToolbarFilter[] => [
    {
      id: 'framework',
      label: 'Framework',
      icon: <AccountTreeIcon style={{ fontSize: 14 }} />,
      options: frameworkOptions,
      value: framework,
      multiple: true,
      onChange: (value) => {
        setFilter('framework', value);
        setPage(0);
      },
    },
    {
      id: 'status',
      label: 'Status',
      icon: <TrendingUpIcon style={{ fontSize: 14 }} />,
      options: STATUS_OPTIONS,
      value: status,
      multiple: true,
      onChange: (value) => {
        setFilter('status', value);
        setPage(0);
      },
    },
    {
      id: 'category',
      label: 'Category',
      icon: <CategoryIcon style={{ fontSize: 14 }} />,
      options: COMPLIANCE_CATEGORY_FILTER_OPTIONS,
      value: category,
      multiple: true,
      onChange: (value) => {
        setFilter('category', value);
        setPage(0);
      },
    },
  ],
    [
      framework,
      frameworkOptions,
      status,
      category,
      setFilter,
    ]
  );

  const handleResetFiltersBase = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );
  const handleResetFilters = () => {
    handleResetFiltersBase();
    setSortParams(null);
    setResetSortFlag((prev) => !prev);
  };

  const handleSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    setSortParams(direction ? { column: columnId, sort: direction } : null);
    setPage(0);
  };

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Security Check',
        sortable: true,
        render: (row) => (
          <TruncatedTitle text={(row as unknown as WazuhComplianceControlItem).title} />
        ),
      },
      {
        id: 'framework',
        label: 'Framework',
        sortable: true,
        render: (row) => (
          <FrameworkTags values={(row as unknown as WazuhComplianceControlItem).framework} />
        ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => (
          <EdrStatusPill
            tone={complianceStatusToTone((row as unknown as WazuhComplianceControlItem).status)}
            label={toComplianceTitle((row as unknown as WazuhComplianceControlItem).status)}
          />
        ),
      },
      {
        id: 'category',
        label: 'Category',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhComplianceControlItem).category}
          </span>
        ),
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        sortable: true,
        render: (row) => (
          <span style={edrMonoStyle}>
            {(row as unknown as WazuhComplianceControlItem).endpoint}
          </span>
        ),
      },
      {
        id: 'hostName',
        label: 'Endpoint Label',
        sortable: false,
        render: (row) => (
          <span style={edrMutedStyle}>
            {formatEndpointLabel((row as unknown as WazuhComplianceControlItem).hostName)}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhComplianceControlItem).organization}
          </span>
        ),
      },

      {
        id: 'lastChecked',
        label: 'Last Checked',
        sortable: true,
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhComplianceControlItem).lastChecked} />
        ),
      },
    ],
    []
  );

  const endpointColumns: Column[] = useMemo(
    () => [
      {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => {
          const item = row as unknown as WazuhComplianceEndpointItem;
          const canOpen = Boolean(item.systemWazuhAgentId?.trim());
          return (
            <span style={edrEndpointNameLinkStyle(canOpen)}>
              {item.endpoint ?? item.name ?? '—'}
            </span>
          );
        },
      },
      {
        id: 'hostName',
        label: 'Endpoint Label',
        render: (row) => (
          <span style={edrMutedStyle}>
            {formatEndpointLabel((row as unknown as WazuhComplianceEndpointItem).hostName)}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        render: (row) => {
          const item = row as unknown as WazuhComplianceEndpointItem;
          return (
            <span style={edrMutedStyle}>
              {item.organization ?? item.organizationName ?? '—'}
            </span>
          );
        },
      },
      {
        id: 'complianceScore',
        label: 'Compliance Score',
        render: (row) => {
          const item = row as unknown as WazuhComplianceEndpointItem;
          return <ComplianceScoreCell score={item.complianceScore} />;
        },
      },
      {
        id: 'totalChecks',
        label: 'Total Checks',
        align: 'right',
        render: (row) => (
          <span style={{ color: EDR_COLORS.textSecondary }}>
            {formatEdrNumber((row as unknown as WazuhComplianceEndpointItem).totalChecks)}
          </span>
        ),
      },
      {
        id: 'passed',
        label: 'Passed',
        align: 'right',
        render: (row) => (
          <span style={{ fontWeight: 600, color: EDR_COLORS.success }}>
            {formatEdrNumber((row as unknown as WazuhComplianceEndpointItem).passed)}
          </span>
        ),
      },
      {
        id: 'failed',
        label: 'Failed',
        align: 'right',
        render: (row) => {
          const failed = (row as unknown as WazuhComplianceEndpointItem).failed ?? 0;
          return (
            <span
              style={{
                fontWeight: 600,
                color: failed > 0 ? EDR_COLORS.critical : EDR_COLORS.textSecondary,
              }}
            >
              {formatEdrNumber(failed)}
            </span>
          );
        },
      },
      {
        id: 'needsReview',
        label: 'Needs Review',
        align: 'right',
        render: (row) => {
          const needsReview = (row as unknown as WazuhComplianceEndpointItem).needsReview ?? 0;
          return (
            <span
              style={{
                fontWeight: 600,
                color: needsReview > 0 ? EDR_COLORS.medium : EDR_COLORS.textSecondary,
              }}
            >
              {formatEdrNumber(needsReview)}
            </span>
          );
        },
      },
      {
        id: 'highestRisk',
        label: 'Highest Risk',
        render: (row) => {
          const risk = (row as unknown as WazuhComplianceEndpointItem).highestRisk;
          if (!risk?.trim()) return <span style={edrMutedStyle}>—</span>;
          return (
            <EdrSeverityPill
              level={complianceRiskToLevel(risk)}
              label={toComplianceTitle(risk) || risk}
            />
          );
        },
      },
      {
        id: 'lastChecked',
        label: 'Last Checked',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhComplianceEndpointItem).lastChecked} />
        ),
      },
    ],
    []
  );

  const endpointComplianceToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: EDR_COLORS.textPrimary,
        }}
      >
        Endpoint Compliance
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(complianceEndpointsTotal)} endpoints evaluated
      </Typography>
    </Box>
  );

  const complianceScore = summary?.overallComplianceScore ?? 0;
  const scoreAccent = framework ? EDR_COLORS.brand600 : EDR_COLORS.success;

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Configuration Compliance')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Configuration Compliance"
          subtitle="Monitor security configuration compliance across all managed endpoints."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canList ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.configurationCompliance" />
        ) : null}

        {canSummary ? (
        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Overall Compliance Score"
            value={`${formatEdrNumber(summary?.overallComplianceScore)}%`}
            icon={<TrendingUpIcon style={{ fontSize: 18 }} />}
            iconColor={scoreAccent}
            accent={scoreAccent}
          >
            <Box width="100%" mt={0.5}>
              <LinearProgress
                variant="determinate"
                value={complianceScore}
                style={{ height: 4, borderRadius: 2, background: EDR_COLORS.slate200 }}
              />
            </Box>
          </EdrKpiCard>
          <EdrKpiCard
            label="Compliant Endpoints"
            value={formatEdrNumber(summary?.compliantEndpoints)}
            icon={<ComputerIcon style={{ fontSize: 18 }} />}
          />
          <EdrKpiCard
            label="Non-Compliant Endpoints"
            value={formatEdrNumber(summary?.nonCompliantEndpoints)}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          />
          <EdrKpiCard
            label="Failed Security Checks"
            value={formatEdrNumber(summary?.failedSecurityChecks)}
            icon={<ReportIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
            accent={EDR_COLORS.high}
          />
        </Box>
        ) : null}

        {canEndpointCompliance ? (
        <EdrDataCard toolbar={endpointComplianceToolbar}>
          <Table
            columns={endpointColumns}
            data={complianceEndpoints as unknown as TableData[]}
            loading={loading}
            showPagination={false}
            onRowClick={(row) => {
              const item = row as unknown as WazuhComplianceEndpointItem;
              const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
              if (!systemWazuhAgentId) return;
              navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                state: { endpointName: item.endpoint ?? item.name },
              });
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}

        {canList ? (
        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Controls…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onSort={handleSort}
            resetSortFlag={resetSortFlag}
            onRowClick={(row) => openComplianceDetail(row as unknown as WazuhComplianceControlItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default ConfigurationCompliance;
